import type { GroupedGuidedPanelId } from './groupedGuidedWorkflow';

/** Browser-only UI prefs for grouped recurring-risk review (not workflow truth). */
export const GROUPED_REVIEW_UI_STORAGE_PREFIX = 'incidents.groupedReviewUi.v1';
export const GROUPED_REVIEW_RESUME_STORAGE_KEY = 'incidents.groupedReviewResume.v1';

export type GroupedReviewUiSnapshot = {
  expanded_panel?: GroupedGuidedPanelId | '';
  risk_context_viewed_at?: string;
  dismissed_helper_ids?: string[];
};

export function groupedReviewUiStorageKey(groupKey: string): string {
  return `${GROUPED_REVIEW_UI_STORAGE_PREFIX}:group:${String(groupKey || '').trim()}`;
}

const GROUPED_GUIDED_PANEL_IDS: GroupedGuidedPanelId[] = [
  'understand_risk',
  'verify_evidence',
  'decide_action',
  'track_impact',
  'report',
];

/**
 * Restore last expanded panel from browser storage, except reopening Verify evidence
 * before representative review is complete (Epic 93 starts at Understand risk).
 */
export function resolveGroupedGuidedInitialPanelId(
  snapshot: GroupedReviewUiSnapshot,
  options?: { representativeReviewComplete?: boolean },
): GroupedGuidedPanelId | undefined {
  const stored = String(snapshot.expanded_panel || '').trim() as GroupedGuidedPanelId;
  if (!GROUPED_GUIDED_PANEL_IDS.includes(stored)) return undefined;
  if (stored === 'track_impact' || stored === 'report') return undefined;
  if (stored === 'verify_evidence' && !options?.representativeReviewComplete) {
    return undefined;
  }
  return stored;
}

export function loadGroupedReviewUiSnapshot(groupKey: string): GroupedReviewUiSnapshot {
  if (typeof window === 'undefined' || !groupKey) return {};
  try {
    const raw = window.localStorage.getItem(groupedReviewUiStorageKey(groupKey));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GroupedReviewUiSnapshot;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function saveGroupedReviewUiSnapshot(
  groupKey: string,
  patch: Partial<GroupedReviewUiSnapshot>,
): GroupedReviewUiSnapshot {
  if (typeof window === 'undefined' || !groupKey) return {};
  const current = loadGroupedReviewUiSnapshot(groupKey);
  const next: GroupedReviewUiSnapshot = { ...current, ...patch };
  try {
    window.localStorage.setItem(groupedReviewUiStorageKey(groupKey), JSON.stringify(next));
  } catch {
    // ignore quota / privacy mode
  }
  return next;
}

export function clearGroupedReviewUiSnapshot(groupKey: string): void {
  if (typeof window === 'undefined' || !groupKey) return;
  try {
    window.localStorage.removeItem(groupedReviewUiStorageKey(groupKey));
  } catch {
    // ignore
  }
}

/** Weak hint when the user previously opened risk context in this browser only. */
export function formatGroupedReviewLocalViewHint(viewedAt?: string | null): string | null {
  const raw = String(viewedAt || '').trim();
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return 'Previously viewed in this browser';
  }
  return `Previously viewed in this browser · ${date.toLocaleString()}`;
}

/** Legacy journey key may have step0; migrate to viewed_at hint only. */
export type GroupedReviewResumeSnapshot = {
  group_key: string;
  representative_incident_id?: string;
  expanded_panel?: GroupedGuidedPanelId | '';
  saved_at: string;
};

export function saveGroupedReviewResumeSnapshot(args: {
  groupKey: string;
  representativeIncidentId?: string;
  expandedPanel?: GroupedGuidedPanelId | '';
}): void {
  if (typeof window === 'undefined') return;
  const groupKey = String(args.groupKey || '').trim();
  if (!groupKey) return;
  const payload: GroupedReviewResumeSnapshot = {
    group_key: groupKey,
    representative_incident_id: String(args.representativeIncidentId || '').trim() || undefined,
    expanded_panel: args.expandedPanel || '',
    saved_at: new Date().toISOString(),
  };
  try {
    window.sessionStorage.setItem(GROUPED_REVIEW_RESUME_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / privacy mode
  }
}

export function loadGroupedReviewResumeSnapshot(): GroupedReviewResumeSnapshot | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(GROUPED_REVIEW_RESUME_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GroupedReviewResumeSnapshot;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!String(parsed.group_key || '').trim()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readLegacyRiskContextViewedAt(groupKey: string): string | undefined {
  if (typeof window === 'undefined' || !groupKey) return undefined;
  const ui = loadGroupedReviewUiSnapshot(groupKey);
  if (ui.risk_context_viewed_at) return ui.risk_context_viewed_at;

  try {
    const legacyKey = `incidents.reviewJourneyProgress.v1:group:${groupKey}`;
    const raw = window.localStorage.getItem(legacyKey);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { completed_step_ids?: string[] };
    const ids = Array.isArray(parsed?.completed_step_ids) ? parsed.completed_step_ids : [];
    if (ids.includes('step0')) {
      return new Date().toISOString();
    }
  } catch {
    // ignore
  }
  return undefined;
}
